import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import AppService from '../../../../Configurations/Appservice';
import { useDispatch } from 'react-redux';
import {
  showLoader,
  dismissLoader,
  showSuccessMessage,
} from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import VerticalFormDialog from './VerticalFormDialog';

/**
 * Verticals Management Page
 * Lists all verticals with CRUD operations
 * Uses mock data during UI-First phase
 */
const VerticalsPage = () => {
  const dispatch = useDispatch();
  const [verticals, setVerticals] = useState([]);
  const [filteredVerticals, setFilteredVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Load verticals from API
  useEffect(() => {
    loadVerticals();
  }, []);

  const loadVerticals = () => {
    setLoading(true);
    AppService.get('api/verticals/list-details')
      .then((response) => {
        setVerticals(response || []);
        setFilteredVerticals(response || []);
      })
      .catch((error) => {
        console.error('Failed to load verticals', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Filter verticals based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = verticals.filter(v =>
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.country || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredVerticals(filtered);
    } else {
      setFilteredVerticals(verticals);
    }
  }, [searchTerm, verticals]);

  const handleAddClick = () => {
    setSelectedVertical(null);
    setDialogOpen(true);
  };

  const handleEditClick = (vertical) => {
    setSelectedVertical(vertical);
    setDialogOpen(true);
  };

  const handleDeleteClick = (vertical) => {
    setSelectedVertical(vertical);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setSelectedVertical(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedVertical(null);
  };

  const handleSave = async (verticalData) => {
    dispatch(showLoader());
    if (selectedVertical) {
      // Update existing
      AppService.put(`api/verticals/${selectedVertical.id}`, verticalData)
        .then(() => {
          dispatch(showSuccessMessage('Vertical updated successfully'));
          loadVerticals();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to update vertical', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    } else {
      // Add new
      AppService.post('api/verticals', verticalData)
        .then(() => {
          dispatch(showSuccessMessage('Vertical created successfully'));
          loadVerticals();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to create vertical', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  const handleDelete = async () => {
    dispatch(showLoader());
    AppService.delete(`api/verticals/${selectedVertical.id}`)
      .then(() => {
        dispatch(showSuccessMessage('Vertical deleted successfully'));
        loadVerticals();
        setDeleteConfirmOpen(false);
        setSelectedVertical(null);
      })
      .catch((error) => {
        console.error('Failed to delete vertical', error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon color="primary" />
            Verticals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure service verticals (UAE PRO, Saudi PRO, Healthcare, etc.)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Vertical
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search verticals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Country</TableCell>
              <TableCell align="center">Categories</TableCell>
              <TableCell align="center">Doc Types</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredVerticals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No verticals found
                </TableCell>
              </TableRow>
            ) : (
              filteredVerticals.map((vertical) => (
                <TableRow key={vertical.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {vertical.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{vertical.name}</Typography>
                      {vertical.nameAr && (
                        <Typography variant="caption" color="text.secondary" dir="rtl">
                          {vertical.nameAr}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{vertical.countryFlag || '🌍'}</span>
                      <Typography variant="body2">{vertical.countryName || vertical.country}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={vertical.categoryCount}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={vertical.documentTypeCount}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={vertical.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={vertical.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditClick(vertical)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(vertical)}
                        disabled={vertical.categoryCount > 0}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {filteredVerticals.length} verticals
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active: {filteredVerticals.filter(v => v.isActive).length}
        </Typography>
      </Box>

      {/* Form Dialog */}
      <VerticalFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        vertical={selectedVertical}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Delete Vertical</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the vertical "{selectedVertical?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerticalsPage;
